import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import PaperIcon from "./ui/PaperIcon";
import ChatIcon from "./ui/ChatIcon";
import { Common } from "@src/styles/Common";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePostFreeTrial } from "./api";
import { AuthStatus, useAuthorization } from "./utils/useAuthorization";
import { renderOverlay } from "./utils/authOverlay";
import MgDialog from "@src/components/Dialog";
import { DialogContent } from "./ui/Authority";
import MissgoAIGuide from "./components/MissgoAIGuide";
import MissgoAIChatbot from "./components/MissgoAIChatbot";
import { TicketPackageListResponseDto } from "@src/hooks/deungi/dto/PackageList.dto";
import { IProduct } from "@src/hooks/stateLand/useStateLand";
import { getUserSubs } from "./utils/useGetSubsState";
import { useQueryTicketPackageListByType } from "@src/hooks/deungi/useTicketPackageList";
import { checkDevice } from "@src/components/deungi/common/utils";
import { PRODUCT_SALE_TYPE, TICKET_TYPE_STRING } from "@src/components/deungi/common/enum";
import { transferToNative, WebAppBridge } from "@src/lib/WebAppBridge";
import { handlePlatformPayment } from "../ownerCheck/utils/paymentUtils";
import { useQueryClient } from "react-query";
import CloseGuide from "@src/assets/CloseGuide.png";
import Arrow from "@src/assets/arrow";

const initialTicketPackage = {
    name: "",
    code: "",
    type: "",
    originalPrice: 0,
    discountPrice: 0,
    discountString: "",
    quantity: 0,
};

const MissgoAI = () => {
    const searchParams = useSearchParams();
    const queryClient = useQueryClient();
    const MissgoAIType = searchParams[0].get("MissgoAIType") as "default" | "chat";
    const deviceType = checkDevice();

    const [missgoAIType, setMissgoAIType] = useState<"default" | "chat">(MissgoAIType ?? "default");
    const [ticketItem, setTicketItem] = useState<TicketPackageListResponseDto>(initialTicketPackage);

    const { mutate: postFreeTrialMutate } = usePostFreeTrial();
    const userSubs = getUserSubs();
    const { status, AISummaryData, AISummaryDataRefetch, isLogin, chatAvailableCountRefetch } = useAuthorization({
        missgoAIType,
        userSubs,
    });

    const { data: TicketList } = useQueryTicketPackageListByType(
        deviceType === "ios" ? PRODUCT_SALE_TYPE.IOS : PRODUCT_SALE_TYPE.AOS,
    );

    const isInitialAnimationRef = useRef(true);

    const [showClose, setShowClose] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            isInitialAnimationRef.current = false;
            const url = new URL(window.location.href);
            url.searchParams.set("isAnimationEnabled", "false");

            window.history.replaceState(null, "", url.toString());
        }, 350);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (TicketList?.list?.length > 0) {
            TicketList?.list
                ?.filter((el: TicketPackageListResponseDto) => el.type === "missgo_ai_chat_ticket")
                .map((item: TicketPackageListResponseDto, idx: number) => {
                    if (idx === 0) {
                        setTicketItem(item);
                    }
                });
        }
    }, [TicketList]);

    useEffect(() => {
        if (isLogin) {
            AISummaryDataRefetch();
        }
    }, [isLogin, AISummaryDataRefetch]);

    // 내리면 닫혀요!
    useEffect(() => {
        const Timer = setTimeout(() => {
            setShowClose(false);
        }, 5000);
        return () => clearTimeout(Timer);
    }, []);

    const isUIBlocked = status !== AuthStatus.SUCCESS;
    const isContentBlurred =
        status === AuthStatus.NOT_LOGGED_IN ||
        (status === AuthStatus.NEEDS_FREE_TRIAL && !AISummaryData) ||
        (status === AuthStatus.NEEDS_SUBSCRIPTION && !AISummaryData) ||
        status === AuthStatus.ANALYSIS_IN_PROGRESS ||
        status === AuthStatus.NEW_ITEM_ERROR ||
        status === AuthStatus.NEEDS_CHATBOT_FREE_TRIAL ||
        status === AuthStatus.NEEDS_CHATBOT_SUBSCRIPTION ||
        status === AuthStatus.NEEDS_CHATBOT_UPGRADE ||
        status === AuthStatus.NEEDS_CHATBOT_TICKET ||
        status === AuthStatus.NEEDS_CHATBOT_TICKET_UPGRADE;

    const ChangeMissgoAIType = (type: "default" | "chat") => {
        setMissgoAIType(type);
        const url = new URL(window.location.href);
        url.searchParams.set("MissgoAIType", String(type));

        window.history.replaceState(null, "", url.toString());
    };

    const onPaymentButtonClick = (type?: "upgrade" | "ticket") => {
        if (type === "upgrade") {
            transferToNative(JSON.stringify({ type: "Subs" }), "onAIAuthorityModalOpen");
        }

        if (type === "ticket") {
            handlePlatformPayment(deviceType, ticketItem, TICKET_TYPE_STRING.MISSGO_AI_CHATBOT_TICKET);
        }
    };

    return (
        <MissgoAIContainer>
            {showClose && (
                <GuidePopUp>
                    <ArrowWrapper>
                        <Arrow />
                    </ArrowWrapper>
                    <img src={CloseGuide} alt="아래로 내리면 닫혀요!" className="Closeimage" />
                    <div className="text">아래로 내리면 닫혀요!</div>
                </GuidePopUp>
            )}
            <ToggleContainer>
                <ToggleBox>
                    <ActiveIndicator $activeType={missgoAIType} />
                    <ToggleButton $isActive={missgoAIType === "default"} onClick={() => ChangeMissgoAIType("default")}>
                        <div className="text">AI 경매 길잡이</div>
                        <PaperIcon color={missgoAIType === "default" ? "#FFF" : "rgba(55, 56, 60, 0.61)"} />
                    </ToggleButton>
                    <ToggleButton $isActive={missgoAIType === "chat"} onClick={() => ChangeMissgoAIType("chat")}>
                        <div className="text">AI 챗봇</div>
                        <ChatIcon color={missgoAIType === "chat" ? "#FFF" : "rgba(55, 56, 60, 0.61)"} />
                    </ToggleButton>
                </ToggleBox>
            </ToggleContainer>

            <ContentWrapper style={{ height: "calc(100vh - 75px)" }}>
                <BlurBox
                    $isBlurred={isContentBlurred}
                    style={{
                        overflowY: missgoAIType === "default" ? "auto" : "hidden",
                    }}
                >
                    {missgoAIType === "default" ? (
                        <MissgoAIGuide summaryData={AISummaryData?.summary?.data} isSuccess={status === AuthStatus.SUCCESS} />
                    ) : (
                        <div style={{ padding: "0 4px 0 14px" }}>
                            <MissgoAIChatbot onPaymentButtonClick={onPaymentButtonClick} />
                        </div>
                    )}
                </BlurBox>
                {isUIBlocked && (
                    <Overlay>
                        {renderOverlay({
                            status,
                            onPaymentButtonClick,
                            postFreeTrialMutate,
                        })}
                    </Overlay>
                )}
            </ContentWrapper>
        </MissgoAIContainer>
    );
};

export default MissgoAI;

const MissgoAIContainer = styled.div`
    height: 100%;
    position: relative;
`;

const Title = styled.h1`
    width: 100%;
    color: #0c002c;
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;

    & > div {
        display: flex;
        flex-direction: row;
        justify-content: center;
        gap: 10px;
    }

    .IconLayout {
        width: 32px;
        height: 32px;
        background-color: ${Common.colors.aucMarkerColor};
        border-radius: 5px;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
    }
`;

const ContentWrapper = styled.div`
    flex-grow: 1;
    position: relative;
`;

const BlurBox = styled.div<{ $isBlurred: boolean }>`
    height: 100%;
    filter: blur(${(props) => (props.$isBlurred ? "10px" : "0px")});
`;

const Overlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
`;

const ToggleContainer = styled.div`
    position: sticky;
    top: 0;
    z-index: 1000;
    background-color: white;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 14px;
`;

const ToggleBox = styled.div`
    position: relative;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border-radius: 8px;
    background: var(--Fill-Normal, rgba(112, 115, 124, 0.08));
`;

const ActiveIndicator = styled.div<{ $activeType: "default" | "chat" }>`
    position: absolute;
    top: 2px;
    left: 2px;
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    border-radius: 8px;
    background-color: ${({ $activeType }) => ($activeType === "default" ? Common.colors.aucMarkerColor : "#000")};
    transition: transform 0.2s ease-in-out, background-color 0.2s ease-in-out;
    transform: ${({ $activeType }) => ($activeType === "default" ? "translateX(0%)" : "translateX(100%)")};
`;

const ToggleButton = styled.button<{ $isActive: boolean }>`
    width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    padding: 8px 0;
    border-radius: 8px;
    background: transparent;
    z-index: 1;
    color: ${({ $isActive }) => ($isActive ? "#FFF" : "rgba(55, 56, 60, 0.61)")};
    transition: color 0.2s ease-in-out;

    .text {
        display: flex;
        justify-content: center;
        align-items: center;

        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 140%;
    }
`;

const ArrowWrapper = styled.div`
    position: absolute;
    bottom: 28%;
    right: 77%;
`;

const GuidePopUp = styled.div`
    position: absolute;
    top: 7.5%;
    left: 60%;
    z-index: 2000;
    width: 126px;

    img {
        width: 100%;
        height: auto;
    }

    .text {
        position: absolute;
        top: 20%;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 140%;
        width: 100%;
        text-align: center;
    }
`;
